import { ComparisonDatum, ComparisonSource } from 'ComponentLibrary/src/molecules/ComparisonChart/types'
import { ResponsiveSwarmPlot, SwarmPlotLayer } from '@nivo/swarmplot'
import { alpha, Box, Stack, Tooltip } from '@mui/material'
import { Typography } from 'ComponentLibrary/src/atoms/typography'
import { TextWithEllipsis } from 'ComponentLibrary/src/molecules/TextWithEllipsis'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { isEmpty, maxBy, minBy } from 'lodash'
import { ComponentProps } from 'react'
import { decimalToLocalizedString, formatNumberWithShortAbbreviation, pixelToNumber } from 'ComponentLibrary/src/common/numbers'
import { getSourceColor } from 'ComponentLibrary/src/molecules/ComparisonChart/constants'
import { ComparisonChartReference as CustomReferenceLayer } from 'ComponentLibrary/src/molecules/ComparisonChart/ComparisonChartReference'
import { ComparisonChartRange as CustomRangeLayer } from 'ComponentLibrary/src/molecules/ComparisonChart/ComparisonChartRange'
import { ComparisonChartAnnotation as CustomAnnotationLayer } from 'ComponentLibrary/src/molecules/ComparisonChart/ComparisonChartAnnotation'
import { ComparisonChartCircle } from 'ComponentLibrary/src/molecules/ComparisonChart/ComparisonChartCircle'
import { ComparisonChartTooltip } from 'ComponentLibrary/src/molecules/ComparisonChart/ComparisonChartTooltip'
import { ComparisonChartTick } from 'ComponentLibrary/src/molecules/ComparisonChart/ComparisonChartTick'
import { ComparisonChartLegend } from 'ComponentLibrary/src/molecules/ComparisonChart/ComparisonChartLegend'
import { InfoTooltip } from 'ComponentLibrary/src/atoms/IconTooltip'
import { ArrowsClockwise } from '@phosphor-icons/react'
import { CounterDot } from 'ComponentLibrary/src/piazza/content/CounterDot/CounterDot'
import { withNivoTooltipWrapperTheme } from 'ComponentLibrary/src/common/nivoTheme'

const ROW_HEIGHT = 68
const TITLE_PERCENTAGE = 40

type ComparisonChartProps = {
    unit: string
    reference?: number
    referenceUnit?: string
    sources: ComparisonSource[]
    forceLegendOpen?: boolean
    copy?: Partial<ComparisonChartCopy>
    locale?: string
}

export type ComparisonChartCopy = {
    emptyLabel: string
    generatedValuesTooltip: string
    sourceDateWarning: string
    relevantDataPoints: (count: number) => string
    totalDataPoints: (count: number) => string
    unknownDataPointCount: string
    unnamedDataSeries: string
}

const DEFAULT_COMPARISON_CHART_COPY: ComparisonChartCopy = {
    emptyLabel: 'Keine Daten',
    generatedValuesTooltip:
        'Diese Werte wurden automatisch aus einer verwandten Datenreihe berechnet. (z.B. Rendite zu Multiplikator oder Monatsmiete zu Jahresmiete)',
    sourceDateWarning: 'Quelle älter als 12 Monate',
    relevantDataPoints: (count) => `${count} ${count > 1 ? 'relevante Datenpunkte' : 'relevanter Datenpunkt'}`,
    totalDataPoints: (count) => `${count} gesamt`,
    unknownDataPointCount: 'Datenpunktanzahl unbekannt',
    unnamedDataSeries: 'Unbenannte Datenreihe',
}

export const ComparisonChart = ({
    unit,
    reference,
    referenceUnit,
    sources,
    forceLegendOpen,
    copy,
    locale = 'de-DE',
}: ComparisonChartProps) => {
    if (isEmpty(sources)) {
        return null // An empty state is currently not needed
    }

    const resolvedCopy = { ...DEFAULT_COMPARISON_CHART_COPY, ...copy }

    const format = (value: number) =>
        value >= 100 ? formatNumberWithShortAbbreviation(value, 1, locale) : decimalToLocalizedString(value, false, 2, 2, locale)

    const groups = sources.flatMap((source) => source.entries.map((_, entryIndex) => `${source.index}-${entryIndex}`))
    const data: ComparisonDatum[] = sources.flatMap((source) =>
        source.entries.flatMap((entry, dataIndex) =>
            entry.values.map((point, valueIndex) => ({
                unit,
                locale,
                reference,
                referenceUnit,
                group: `${source.index}-${dataIndex}`,
                id: `${source.index}-${dataIndex}-${valueIndex}`,
                label: point.label,
                value: point.value,
                color: getSourceColor(source.index),
                formattedReference: reference !== undefined ? format(reference) : undefined,
            }))
        )
    )

    const allValues = [...data, { value: reference }]
    const maxValue = maxBy(allValues, 'value')?.value || 0
    const minValue = minBy(allValues, 'value')?.value || 0
    const hasAtLeastOneNonZeroValue = allValues.some(({ value }) => !!value)

    const layers: SwarmPlotLayer<ComparisonDatum>[] = [
        'grid',
        'axes',
        CustomReferenceLayer,
        CustomRangeLayer,
        CustomAnnotationLayer,
        'circles',
    ]

    return (
        <Stack direction="column" width="100%" position="relative" gap={tokens.SizingSpacing150}>
            <Stack direction="row" justifyContent="flex-end">
                <Stack direction="column" width="100%" position="absolute">
                    {sources.map((source) =>
                        source.entries.map((entry, entryIndex) => (
                            <Stack
                                direction="row"
                                key={`entry-${source.index}-${entry.index}`}
                                height={`${ROW_HEIGHT}px`}
                                position="relative"
                                sx={{ ':nth-child(odd)': { backgroundColor: alpha(tokens.ColorGraphNeutralLightest, 0.25) } }}
                            >
                                <Stack
                                    direction="row"
                                    width={`${TITLE_PERCENTAGE}%`}
                                    alignItems="center"
                                    paddingLeft={tokens.SizingSpacing200}
                                    gap={tokens.SizingSpacing075}
                                    zIndex={1}
                                >
                                    <Stack direction="column" maxWidth="calc(100% - 100px)">
                                        <TextWithEllipsis
                                            tooltipText={source.publisher}
                                            typographyProps={{ variant: 'captionSmall', color: tokens.ColorTextBoldest }}
                                        >
                                            {source.publisher}
                                        </TextWithEllipsis>
                                        {entry.shortDescription && (
                                            <TextWithEllipsis
                                                tooltipText={entry.shortDescription}
                                                typographyProps={{ variant: 'bodyTextSmall', color: tokens.ColorTextSubtle }}
                                            >
                                                {entry.shortDescription}
                                            </TextWithEllipsis>
                                        )}
                                    </Stack>
                                    <CounterDot number={source.index + 1} />
                                    {source.entries.length > 1 && (
                                        <CounterDot
                                            variant="secondary"
                                            number={source.index + 1}
                                            overwrite={String.fromCharCode(65 + entryIndex)}
                                        />
                                    )}
                                    {entry.footNote && <InfoTooltip text={entry.footNote} />}
                                    {entry.isAutoConverted && (
                                        <Tooltip title={resolvedCopy.generatedValuesTooltip}>
                                            <ArrowsClockwise
                                                size={tokens.SizingIconS}
                                                color={tokens.ColorIconWarning}
                                                style={{ flexShrink: 0 }}
                                                weight="bold"
                                                cursor="help"
                                            />
                                        </Tooltip>
                                    )}
                                </Stack>
                                <Stack direction="row" width={`${100 - TITLE_PERCENTAGE}%`} justifyContent="center" alignItems="center">
                                    {isEmpty(entry.values) && (
                                        <Typography variant="bodyTextSmall" color={tokens.ColorGraphNeutralLight}>
                                            {resolvedCopy.emptyLabel}
                                        </Typography>
                                    )}
                                </Stack>
                                {entry.endAdornment && (
                                    <Box zIndex={1} alignSelf="center" padding={tokens.SizingSpacing050}>
                                        {entry.endAdornment}
                                    </Box>
                                )}
                            </Stack>
                        ))
                    )}
                </Stack>
                <Box
                    width={`${100 - TITLE_PERCENTAGE}%`}
                    height={`${groups.length * ROW_HEIGHT + pixelToNumber(tokens.SizingSpacing300)}px`}
                    marginTop="-24px"
                    sx={{
                        '.tick': { fill: tokens.ColorTextInverse, transition: 'fill ease-in 0.3s' },
                        '&:hover': { '.tick': { fill: tokens.ColorGraphNeutralLight } },
                    }}
                >
                    <ResponsiveSwarmPlot<ComparisonDatum>
                        layout="horizontal"
                        margin={{
                            bottom: pixelToNumber(tokens.SizingSpacingNone),
                            top: pixelToNumber(tokens.SizingSpacing300),
                            right: pixelToNumber(tokens.SizingSpacing075),
                            left: pixelToNumber(tokens.SizingSpacing300),
                        }}
                        theme={chartTheme}
                        layers={layers as []}
                        isInteractive={true}
                        data={data}
                        groups={groups}
                        value="value"
                        valueFormat={(value) => (typeof value === 'number' ? format(value) : '')}
                        valueScale={{ type: 'linear', min: minValue * 0.75, max: maxValue * 1.1 }}
                        tooltip={ComparisonChartTooltip}
                        circleComponent={ComparisonChartCircle}
                        gridXValues={hasAtLeastOneNonZeroValue ? 6 : 0}
                        enableGridY={false}
                        axisTop={hasAtLeastOneNonZeroValue ? { tickValues: 6, format, renderTick: ComparisonChartTick } : null}
                        axisLeft={null}
                        axisBottom={null}
                        axisRight={null}
                    />
                </Box>
            </Stack>
            <ComparisonChartLegend sources={sources} forceLegendOpen={forceLegendOpen} copy={resolvedCopy} />
        </Stack>
    )
}

const chartTheme: ComponentProps<typeof ResponsiveSwarmPlot>['theme'] = withNivoTooltipWrapperTheme({
    grid: {
        line: {
            stroke: tokens.ColorGraphNeutralLightest,
            strokeWidth: 1,
            strokeDasharray: '8 2',
        },
    },
})
